import { DatePickerProps } from 'antd'
import { RangePickerProps } from 'antd/es/date-picker'
import dayjs, { ConfigType, Dayjs } from 'dayjs'
import { isFunction, isString } from 'lodash'

import { OddEvenValue } from '@eastcoal/common/types/component'
import { toArray } from '@eastcoal/common/utils/array'
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@eastcoal/common/utils/date'

import { FormatType, ValueType } from './interface'

const parseDate = (value: ConfigType, format: FormatType) => {
  const result = dayjs(value, typeof format === 'string' ? format : undefined)
  // 返回 invalid date 时 RangePicker 会死循环
  return result.isValid() ? result : undefined
}

export const parseValue = (
  value: ValueType | [ValueType, ValueType],
  format: FormatType | FormatType[],
  isRange: boolean
) => {
  let results: (Dayjs | undefined)[] = []

  if (value) {
    const values = toArray(value)
    const formats = toArray(format)
    results = values.map((v, i) => parseDate(v, formats[i] ?? formats[0]))
  }

  return isRange ? results : results[0]
}

const formatDate = (value: ValueType, format: FormatType) => {
  if (!value) {
    return null
  }

  if (isFunction(format)) {
    return format(value)
  }

  return isString(value) ? value : value.format(format)
}

export const stringifyValue = <R extends boolean>(
  value: OddEvenValue<ValueType, R>,
  format: OddEvenValue<FormatType, R>,
  isRange: R
) => {
  let results: (string | null)[] = []
  if (value) {
    const values = toArray(value)
    const formats = toArray(format)
    results = values.map((v, i) => formatDate(v, formats[i] ?? formats[0]))
  }
  return isRange ? results : results[0]
}

type MapDateFormatOptions = {
  isRange: boolean
  showTime?: boolean
}

export const mapDateFormat = ({ isRange, showTime }: MapDateFormatOptions) => {
  const getDefaultFormat = (props: DatePickerProps | RangePickerProps) => {
    if (props['picker'] === 'month') {
      return 'YYYY-MM'
    } else if (props['picker'] === 'quarter') {
      return 'YYYY-\\QQ'
    } else if (props['picker'] === 'year') {
      return 'YYYY'
    } else if (props['picker'] === 'week') {
      return 'gggg-wo'
    }
    return showTime || 'showTime' in props ? DATE_TIME_FORMAT : DATE_FORMAT
  }

  return (props: any) => {
    const onChange = props.onChange as any
    const format = props.format || getDefaultFormat(props)

    return {
      ...props,
      format,
      value: parseValue(props.value, format, isRange),
      onChange: (value: any) => {
        const date = stringifyValue(value, format, isRange)
        onChange?.(date)
      }
    }
  }
}
